import React, { useContext } from 'react';
import IconEdit from 'shared/icons/iconEdit';
import IconDownload from 'shared/icons/iconDownload';
import IconRestore from 'shared/icons/iconRestore';
import IconTrash from 'shared/icons/trash';
import { useTranslation } from 'react-i18next';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { GridRowContext } from 'shared/lib/extendedComponents/grids/components/gridRowContainer';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Menu,
  Typography,
  useTheme,
} from '@mui/material';
import { makeTransition } from 'shared/lib/utils/utils';
import CellContainer from 'shared/lib/extendedComponents/grids/components/cellContainer';
import IconGear from 'shared/icons/gear/iconGear';
import { isTouchDevice } from 'shared/lib/utils/globalUtils';
import FavoriteIconController from 'shared/icons/favourite/favoriteIconController';
import IconVerticalDots from 'shared/icons/iconVerticalDots';
import CommonMenuItem from 'shared/lib/components/menu/commonMenuItem';

export default function MobileSettingsCell({
  onDownload,
  onEdit,
  onSettings,
  onRestore,
  onDelete,
  onFavorite,
  isFavorite,
}: {
  onDownload?: () => void;
  onEdit?: () => void;
  onSettings?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
  onFavorite?: () => void;
  isFavorite?: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const isHover = useContext(GridRowContext).isHover || isTouchDevice();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const {
    spacings: { sp },
  } = useContext(UiContext);

  const visibilitySx = isHover
    ? { opacity: 1, pointerEvents: 'all' }
    : { opacity: 0, pointerEvents: 'none' };

  return (
    <CellContainer
      sx={{
        position: 'relative',
        alignItems: 'end',
        cursor: 'pointer',
        overflow: 'visible',
        p: 0,
      }}
    >
      <IconButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <IconVerticalDots
          sizeKey="20x20"
          extColor={theme.palette.text.primary}
        ></IconVerticalDots>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={(e, reason) => {
          // @ts-ignore
          e.preventDefault();
          // @ts-ignore
          e.stopPropagation();
          setAnchorEl(null);
        }}
      >
        {onEdit && (
          <CommonMenuItem
            sx={{ minWidth: 'auto' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnchorEl(null);
              onEdit();
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: 1,
                gap: 4,
              }}
            >
              <IconEdit
                sizeKey="20x20"
                extColor={theme.palette.text.primary}
              ></IconEdit>
              <Typography variant="f16">Редактировать</Typography>
            </Box>
          </CommonMenuItem>
        )}
        {onSettings && (
          <CommonMenuItem
            sx={{ minWidth: 'auto' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnchorEl(null);
              onSettings();
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: 1,
                gap: 4,
              }}
            >
              <IconGear
                sizeKey="20x20"
                extColor={theme.palette.text.primary}
              ></IconGear>
              <Typography variant="f16">Настроить</Typography>
            </Box>
          </CommonMenuItem>
        )}
        {onDownload && (
          <CommonMenuItem
            sx={{ minWidth: 'auto' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnchorEl(null);
              onDownload();
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: 1,
                gap: 4,
              }}
            >
              <IconDownload
                sizeKey="20x20"
                extColor={theme.palette.text.primary}
              ></IconDownload>
              <Typography variant="f16">Скачать</Typography>
            </Box>
          </CommonMenuItem>
        )}
        {onRestore && (
          <CommonMenuItem
            sx={{ minWidth: 'auto' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnchorEl(null);
              onRestore();
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: 1,
                gap: 4,
                justifyContent: 'space-between',
              }}
            >
              <IconRestore
                sizeKey="20x20"
                extColor={theme.palette.text.primary}
              ></IconRestore>
              <Typography variant="f16">Восстановить</Typography>
            </Box>
          </CommonMenuItem>
        )}
        {onDelete && (
          <CommonMenuItem
            sx={{ minWidth: 'auto' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnchorEl(null);
              onDelete();
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: 1,
                gap: 4,
              }}
            >
              <IconTrash
                sizeKey="20x20"
                extColor={theme.palette.text.primary}
              ></IconTrash>
              <Typography variant="f16">Удалить</Typography>
            </Box>
          </CommonMenuItem>
        )}
        {onFavorite && (
          <CommonMenuItem
            sx={{ minWidth: 'auto' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAnchorEl(null);
              onFavorite();
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: 1,
                gap: 4,
              }}
            >
              <FavoriteIconController
                isFavourite={!!isFavorite}
                sizeKey="20x20"
                extColor={theme.palette.text.primary}
              ></FavoriteIconController>
              <Typography variant="f16">
                {isFavorite ? 'Из избранного' : 'В избранное'}
              </Typography>
            </Box>
          </CommonMenuItem>
        )}
      </Menu>
    </CellContainer>
  );
}
